'use client'

import { type FC } from 'react'

import SvgPause from '@/icons/solid/Pause'
import SvgPlay from '@/icons/solid/Play'
import { tw } from '@/lib/utils'

import { AudioProgressRing } from '../audio-progress-ring'
import { useAudioPlayer } from '../use-audio-player'

interface AudioButtonProps {
  url: string
  group?: string
  className?: string
}

export const AudioButton: FC<AudioButtonProps> = ({ url, group, className }) => {
  const { audioRef, status, toggle, isMounted, audioProps } = useAudioPlayer({ url, group })

  if (!isMounted) return null

  return (
    <>
      <button
        type="button"
        data-tracking="ignore"
        onClick={(e) => {
          e.stopPropagation()
          toggle()
        }}
        aria-label={status.isPlaying ? 'Pause audio' : 'Play audio'}
        className={tw(
          'relative aspect-square w-12 shrink-0 cursor-pointer rounded-full border-none bg-white p-0 shadow-[2px_2px_8px_rgba(0,0,0,0.05)]',
          className
        )}
      >
        {/* Inset the ring within the white disc (ring ≈ 0.75 of the button, matching
            the original). The wrapper's 10% inset sizes it (and scales with the
            button); the ring fills the wrapper. */}
        <div className="absolute inset-[10%]">
          <AudioProgressRing size={48} progress={status.progress} className="h-full w-full" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          {status.isPlaying ? (
            <SvgPause aria-hidden className="text-navy h-[13px] w-[13px]" />
          ) : (
            <SvgPlay aria-hidden className="text-navy ml-[3px] h-[13px] w-[13px]" />
          )}
        </div>
      </button>
      <audio ref={audioRef} {...audioProps} />
    </>
  )
}
